/**
 * Authentication types for Dafne client
 */

// User role types
export type UserRole = "diabetic" | "monitor" | "clinician";

export const USER_ROLES = {
  DIABETIC: "diabetic" as const,
  MONITOR: "monitor" as const,
  CLINICIAN: "clinician" as const,
};

// Auth state representation
export interface AuthState {
  isAuthenticated: boolean;
  userId?: string;
  email?: string;
  displayName?: string;
  expiresAt?: Date;
  roles?: UserRole[];
  isAdmin?: boolean;
  isLoading: boolean;
}

// Initial auth state (loading)
export const INITIAL_AUTH_STATE: AuthState = {
  isAuthenticated: false,
  isLoading: true,
};

// Unauthenticated state
export const UNAUTHENTICATED_STATE: AuthState = {
  isAuthenticated: false,
  isLoading: false,
};

// Login request payload
export interface LoginRequest {
  email: string;
  password: string;
  staySignedIn?: boolean;
}

// Registration request payload
export interface RegistrationRequest {
  email: string;
  password: string;
  displayName: string;
  roles?: UserRole[];
}

// Login failure reasons (matches server enum)
export type LoginFailureReason =
  | "none"
  | "invalid_credentials"
  | "account_locked"
  | "account_deleted"
  | "two_factor_required"
  | "two_factor_invalid"
  | "password_change_required"
  | "password_expired"
  | "user_not_found"
  | "unknown_error"
  | "multiple_matching_users"
  | "password_change_token_invalid"
  | "password_not_good_enough"
  | "too_many_attempts"
  | "email_not_verified";

// Auth result from server
export interface AuthResult {
  success: boolean;
  sessionId?: string;
  credentials?: UserCredentials;
  errorMessage?: string;
  errorReason?: LoginFailureReason;
}

// User credentials from successful auth
export interface UserCredentials {
  token: string;
  sessionId: string;
  expiresUtc: string;
  userId: string;
  email: string;
  displayName: string;
  isTwoFactorEnabled: boolean;
  roles: UserRole[];
  isAdmin: boolean;
}

// Minimal auth info stored in localStorage
export interface StoredAuthInfo {
  userId: string;
  email: string;
  displayName: string;
  expiresAt: string; // ISO date string
  roles: UserRole[];
  isAdmin: boolean;
}

// Current user response from API
export interface CurrentUserResponse {
  isAuthenticated: boolean;
  userId?: string;
  email?: string;
  displayName?: string;
  expiresAt?: string;
  roles?: UserRole[];
  isAdmin?: boolean;
}
